import type { LabTest, LabTestId, OcrLine } from '../../types/document'
import { cleanLine, truncate } from './text'

// Medical (lab) reports. Each row is read as: test name, value, optional
// H/L flag, unit, and the reference range PRINTED ON THE REPORT. A value is
// called low/high only against that printed range — never against ranges
// Samjho knows from elsewhere. Rows without a printed range get no status.

const NUM = String.raw`\d+(?:\.\d+)?`
const UNIT = String.raw`(?:g\/dl|gm\/dl|mg\/dl|mg\/l|g\/l|%|u\/l|iu\/l|iu\/ml|[µu]iu\/ml|miu\/l|miu\/ml|ng\/ml|ng\/dl|pg\/ml|[µu]g\/dl|mcg\/dl|nmol\/l|pmol\/l|mmol\/l|meq\/l|fl|pg|mm\/hr|mm\/1st\s*hr|mm\s*at\s*1\s*hr|cells\/cumm|cells\/[µu]l|\/cumm|\/[µu]l|cumm|10\^?3\/[µu]l|10\^?6\/[µu]l|x\s*10\^?\d+\/l|lakhs?\/cumm|lakh\/[µu]l|mill(?:ion)?s?\/cumm|thou\/[µu]l)`

// value [flag] [unit] [range] [unit]
const ROW = new RegExp(
  String.raw`^\s*[:\-–]?\s*(?<value>${NUM})\s*(?<flag>\b(?:H|L|High|Low|HIGH|LOW)\b|\*)?\s*(?<unit1>${UNIT})?\s*` +
    String.raw`(?:(?<lo>${NUM})\s*(?:-|–|—|to)\s*(?<hi>${NUM})|(?:<|≤|upto|up\s*to|less\s*than)\s*(?<max>${NUM})|(?:>|≥|more\s*than|greater\s*than)\s*(?<min>${NUM}))?` +
    String.raw`\s*(?<unit2>${UNIT})?\s*(?<flag2>\b(?:H|L|High|Low|HIGH|LOW)\b)?`,
  'i'
)

/** Common tests, recognised by name (aliases as printed by Indian labs). Order matters: specific before generic. */
export const KNOWN_TESTS: Array<{ id: LabTestId; re: RegExp }> = [
  { id: 'hba1c', re: /hb\s*a1c|glycated\s*h(a)?emoglobin|glycosylated\s*h(a)?emoglobin/i },
  { id: 'haemoglobin', re: /h(a)?emoglobin|\bhb\b|\bhgb\b/i },
  { id: 'wbc', re: /total\s*(leu[ck]ocyte|wbc)\s*count|\btlc\b|\bwbc\b|white\s*blood\s*cell/i },
  { id: 'platelets', re: /platelet|\bplt\b/i },
  { id: 'rbc', re: /\brbc\b|red\s*blood\s*cell/i },
  { id: 'fasting_sugar', re: /(glucose|sugar)\s*[,(-]?\s*fasting|fasting\s*(blood\s*|plasma\s*)?(glucose|sugar)|\bfbs\b|\bfpg\b/i },
  { id: 'pp_sugar', re: /post\s*-?\s*prandial|\bppbs\b|\bpp\s*(blood\s*)?(sugar|glucose)|\bppbg\b/i },
  { id: 'random_sugar', re: /random\s*(blood\s*)?(sugar|glucose)|\brbs\b/i },
  { id: 'ldl', re: /\bldl\b/i },
  { id: 'hdl', re: /\bhdl\b/i },
  { id: 'cholesterol', re: /cholesterol/i },
  { id: 'triglycerides', re: /triglycerides?/i },
  { id: 'creatinine', re: /creatinine/i },
  { id: 'urea', re: /\burea\b|\bbun\b/i },
  { id: 'uric_acid', re: /uric\s*acid/i },
  { id: 'sgpt', re: /sgpt|\balt\b|alanine/i },
  { id: 'sgot', re: /sgot|\bast\b|aspartate/i },
  { id: 'bilirubin', re: /bilirubin/i },
  { id: 'tsh', re: /\btsh\b|thyroid\s*stimulating/i },
  { id: 'vitamin_d', re: /vitamin\s*d\b|25\s*-?\s*(oh|hydroxy)/i },
  { id: 'vitamin_b12', re: /vitamin\s*b\s*-?\s*12|\bb12\b|cobalamin/i },
  { id: 'esr', re: /\besr\b|erythrocyte\s*sedimentation/i }
]

export function knownTest(name: string): LabTestId | null {
  return KNOWN_TESTS.find((t) => t.re.test(name))?.id ?? null
}

/** Words that look like a test name but are headings, patient details or notes. */
const NOT_A_TEST = /^(age|sex|gender|name|patient|ref|referred|sample|collected|reported|registered|date|time|page|mobile|phone|id|uhid|lab|bill|receipt|dr\.?|doctor|method|interpretation|note|comment|remarks?|investigation|test\s*name|result|units?|biological|reference)\b/i

function parseRow(text: string): Omit<LabTest, 'evidence' | 'certainty'> | null {
  const clean = text.replace(/\s+/g, ' ').trim()
  // Name: leading text up to the first number that starts the value (names like
  // "Vitamin B12" or "HbA1c" keep their digits)
  const m = clean.match(/^(?<name>[A-Za-z][A-Za-z0-9 ().,'\/+&-]*?[A-Za-z0-9)])\s*[:\-–]?\s+(?<rest>\d.*)$/)
  if (!m?.groups) return null
  let name = m.groups.name.trim()
  let rest = m.groups.rest
  // "Vitamin B 12 450 ..." / "Vitamin D 25 OH 18 ..." : digits that belong to the name
  const nameTail = rest.match(/^(12|25\s*-?\s*oh)\b\s*(.*)$/i)
  if (nameTail && /vitamin|\bb\s*$/i.test(name)) {
    name = `${name} ${nameTail[1]}`
    rest = nameTail[2]
  }
  if (name.length < 2 || name.length > 60 || NOT_A_TEST.test(name)) return null

  const r = rest.match(ROW)?.groups
  if (!r) return null
  const value = Number(r.value)
  const unit = (r.unit1 || r.unit2 || '').trim()
  const testId = knownTest(name)
  const low = r.lo !== undefined ? Number(r.lo) : r.min !== undefined ? Number(r.min) : null
  const high = r.hi !== undefined ? Number(r.hi) : r.max !== undefined ? Number(r.max) : null
  const hasRange = low !== null || high !== null
  // Unknown names need a printed range or unit to count as a test row (avoids
  // treating "Ward 4" or "Bill No 1234" as results)
  if (!testId && !hasRange && !unit) return null
  if (hasRange && low !== null && high !== null && low > high) return null

  const flag = (r.flag || r.flag2 || '').toUpperCase()
  let status: LabTest['status'] = null
  if (hasRange) {
    status = low !== null && value < low ? 'low' : high !== null && value > high ? 'high' : 'normal'
  } else if (flag === 'H' || flag === 'HIGH') status = 'high'
  else if (flag === 'L' || flag === 'LOW') status = 'low'

  return { name, testId, value, unit, low, high, status }
}

/**
 * Test rows from a lab report, in the order printed. Reads raw OCR lines, not
 * sentence segments: a result row is one table line, and "11.2" must not be
 * split as if it ended a sentence.
 */
export function extractLabTests(lines: OcrLine[]): LabTest[] {
  const seen = new Set<string>()
  const tests: LabTest[] = []
  for (const line of lines) {
    const text = cleanLine(line.text)
    const row = text && parseRow(text)
    if (!row) continue
    const key = `${row.name.toLowerCase()}|${row.value}`
    if (seen.has(key)) continue
    seen.add(key)
    tests.push({ ...row, evidence: truncate(text), certainty: line.confidence < 60 ? 'unclear' : 'clear' })
  }
  return tests
}

/** Does this look like a lab report? Several result rows with units or ranges. */
export function looksLikeLabReport(tests: LabTest[], text: string): boolean {
  const withRange = tests.filter((t) => t.low !== null || t.high !== null).length
  const known = tests.filter((t) => t.testId).length
  const header = /pathology|laborator|diagnostic|lab\s*report|test\s*report|investigation|biological\s*ref|reference\s*(range|interval)|haematology|hematology|biochemistry|blood\s*test|thyroid\s*profile|lipid\s*profile|liver\s*function|kidney\s*function|\bcbc\b|complete\s*blood/i.test(text)
  return (withRange >= 3 && known >= 1) || (header && (withRange >= 2 || known >= 2))
}
